import { useRef, useState } from "react";
import { buildMaterialsList, type MaterialsLine, type MaterialsRow } from "@workspace/api-zod/materials-list";
import { manuallyApprovedMaterials, validMaterialReview, type MaterialAllowanceApproval } from "@workspace/api-zod/material-allowance";
import { MaterialOrderQuantity } from "./material-order-quantity";
import { ChevronDown, AlertTriangle, AlertCircle, Check, Ruler, Eye, HardHat, Hammer, Layers, Building2, Home, TreePine, Paintbrush, Zap, Wrench } from "lucide-react";
import { materialsCategoryGroups } from "./materials-category-groups";
import type { LucideIcon } from "lucide-react";
import "./quote-materials-workspace.css";
import { NewFeatureBadge } from "./new-feature-badge";
import { MaterialsOrderList } from "./materials-order-list";
import { buildMaterialsOrder, materialsConfirmationFingerprint } from "./materials-order-data";
import { useMaterialsConfirmation } from "./use-materials-confirmation";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { materialMeasureUnit, materialPurchaseUnit } from "./material-purchase-units";

const SECTION_ICONS: Record<string, LucideIcon> = {
  Preparation: HardHat, Demolition: Hammer, Substructure: Layers,
  Structure: Building2, Roofing: Home, "External Works": TreePine,
  Finishes: Paintbrush, Services: Zap, Other: Wrench,
};

const gbp = (n: number) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function parsePrice(raw: string): number | string {
  const t = raw.trim();
  if (!t) return "Enter a price";
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return "Enter a price of 0 or more, up to 2 decimal places";
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 99999999.99) return "Price must be between £0.00 and £99,999,999.99";
  return n;
}

function PriceCell({ row, onPriceChange }: { row: MaterialsRow; onPriceChange: (id: string, p: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!row.editable || !row.materialId) return <span data-testid={`text-unit-price-${row.key}`}>{gbp(row.unitPrice)}</span>;
  const id = row.materialId;
  if (draft === null) return <button type="button" data-testid={`button-edit-price-${row.key}`}
    className="underline decoration-dotted underline-offset-4 text-primary py-1"
    aria-label={`Edit unit price for ${row.name}`} onClick={() => { setDraft(row.unitPrice.toFixed(2)); setError(null); }}>
    {gbp(row.unitPrice)}
  </button>;
  const commit = () => {
    const r = parsePrice(draft);
    if (typeof r === "string") { setError(r); return; }
    setDraft(null); setError(null);
    if (Math.round(r * 100) !== Math.round(row.unitPrice * 100)) onPriceChange(id, r);
  };
  return <span className="inline-flex flex-col items-end">
    <span className="inline-flex items-center gap-1">£<input autoFocus inputMode="decimal" type="text"
      data-testid={`input-price-${row.key}`} aria-label={`Unit price for ${row.name}`} aria-invalid={!!error}
      className="h-8 w-24 rounded border bg-background px-2 text-sm text-right" value={draft}
      onChange={e => { setDraft(e.target.value); setError(null); }}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); setDraft(null); setError(null); }
      }} /></span>
    {error && <span role="alert" className="text-destructive text-xs mt-1 max-w-[12rem] text-right">{error}</span>}
  </span>;
}

function Rows({ rows, label, subtotal, roundingExtra, orderAdjustment, testid, props }: {
  rows: MaterialsRow[]; label: string; subtotal: number; roundingExtra: number; orderAdjustment: number; testid: string;
  props: Pick<Props, "onPriceChange" | "onRoundingChange" | "onOrderQuantityChange" | "onJump" | "onApprovalChange" | "saving" | "items">;
}) {
  return <div className="min-w-0" data-testid={`group-${testid}`}>
    <div className="qm-group-heading"><h5>{label}</h5><strong>{gbp(subtotal)}</strong></div>
    {materialsCategoryGroups(rows).map(group => <section key={group.category} className="qm-category" aria-label={`${label} · ${group.category}`}>
    <h6>{(() => { const Icon = SECTION_ICONS[group.section] ?? Wrench; return <Icon size={16} aria-hidden="true" />; })()}<span>{group.category}</span></h6>
    <div className="qm-column-headings" aria-hidden="true"><span>Material</span><span>Order qty</span><span>Buy price</span><span>Material cost</span></div>
    {group.rows.map(row => {
      const purchase = materialPurchaseUnit(row);
      const convertedUnit = purchase.unit !== materialMeasureUnit(row.unit);
      const rounded = row.roundPacks && row.roundedQuantity !== (row.orderQuantity ?? row.quantity);
      const canRound = (row.packSize ?? 0) > 0;
      const accepted = row.contributors.filter(c => manuallyApprovedMaterials(props.items[c.index]));
      return <div key={row.key} data-testid={`row-material-${row.key}`}
        className="qm-material-row">
        <div className="qm-material-name">
          <strong>{row.name}</strong>
          {accepted.length > 0 && <div className="qm-approved-actions">
            <span className="qm-approved-badge">Approved</span>
            {props.onApprovalChange && accepted.map(c => <button type="button" key={c.index}
              disabled={props.saving}
              aria-label={`Undo approval for ${row.name} in ${c.name}`}
              onClick={() => {
                const current = validMaterialReview(props.items[c.index]);
                if (current && manuallyApprovedMaterials(props.items[c.index]))
                  props.onApprovalChange?.(c.index, { ...current, source: "builder", approved: false,
                    manualApproved: false, builderWithheld: true });
              }}>Undo approval{accepted.length > 1 ? ` · ${c.name}` : ""}</button>)}
          </div>}
          <details className="qm-sources">
            <summary>Spec &amp; sources <ChevronDown aria-hidden="true" size={12} /></summary>
            <div>{row.spec && <p>{row.spec}</p>}
              <p>Used in: {row.contributors.map((c, i) => <span key={`${c.index}-${i}`}>{i > 0 && ", "}
                <button type="button" className="underline underline-offset-2 hover:text-primary" data-testid={`link-used-in-${row.key}-${c.index}`}
                  onClick={() => props.onJump(c.index)}>{c.name}</button></span>)}</p>
            </div>
          </details>
        </div>
        <div className="qm-order-quantity">
          <span className="qm-field-label">Order qty</span>
          <MaterialOrderQuantity row={row} onChange={props.onOrderQuantityChange} />
          {canRound && <label className="qm-pack-control">
            <input type="checkbox" checked={row.roundPacks} aria-label={`Round ${row.name} to packs`} data-testid={`toggle-round-${row.key}`}
              onChange={e => props.onRoundingChange(row, e.target.checked)} />
            Round to {row.packSize} {row.unit} packs
          </label>}
        </div>
        <div className="qm-unit-price"><span className="qm-field-label">Buy price / {row.unit}</span>
          <PriceCell row={row} onPriceChange={props.onPriceChange} />
          {convertedUnit && <span className="ml-1 text-[10px] text-muted-foreground">/ {row.unit}</span>}</div>
        <div className="qm-material-cost"><span className="qm-field-label">Material cost</span>
        <span className="tabular-nums font-semibold" data-testid={`text-total-${row.key}`}>
          {gbp(row.total)}
          {rounded && <span className="block text-xs font-normal text-muted-foreground">+{gbp(row.total - (row.orderTotal ?? row.exactTotal))}</span>}
        </span></div>
      </div>;
    })}</section>)}
    {roundingExtra > 0 && <p className="text-xs text-muted-foreground text-right pt-2" data-testid={`text-rounding-${testid}`}>pack rounding +{gbp(roundingExtra)}</p>}
    {orderAdjustment !== 0 && <p className="text-xs text-muted-foreground text-right pt-2">Order quantity adjustment {orderAdjustment > 0 ? "+" : "−"}{gbp(Math.abs(orderAdjustment))}</p>}
    <div className="flex justify-between border-t-2 pt-2 text-sm font-semibold">
      <span>{label} subtotal</span><span className="tabular-nums" data-testid={`text-subtotal-${testid}`}>{gbp(subtotal)}</span>
    </div>
  </div>;
}

type Props = {
  items: MaterialsLine[]; reference?: string; saved: boolean; saving?: boolean;
  quoteId?: string;
  requireApproval?: boolean;
  onPriceChange: (materialId: string, price: number) => void;
  onRoundingChange: (row: MaterialsRow, enabled: boolean) => void;
  onOrderQuantityChange?: (row: MaterialsRow, quantity: number | null) => void;
  onJump: (index: number) => void;
  onApprovalChange?: (index: number, approval: MaterialAllowanceApproval) => void;
};

export function QuoteMaterialsList({ items, reference, quoteId, saving, onPriceChange, onRoundingChange, onOrderQuantityChange, onJump, onApprovalChange, requireApproval = false }: Props) {
  const data = buildMaterialsList(items, requireApproval);
  const order = buildMaterialsOrder(data);
  const { confirmed, confirm } = useMaterialsConfirmation(materialsConfirmationFingerprint(data), quoteId);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const orderRef = useRef<HTMLDivElement>(null);
  const revealAfterClose = useRef(false);
  const showOrder = () => {
    orderRef.current?.focus({ preventScroll: true });
    orderRef.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
  };
  const distinct = new Set([...data.included, ...data.optional].map(r => r.materialId ?? r.name)).size;
  const handlers = { items, saving, onPriceChange, onRoundingChange, onOrderQuantityChange, onJump, onApprovalChange };
  return <><details className="qm-panel qm-list-panel" data-testid="quote-materials-list">
    <summary className="qm-panel-heading">
      <div className="qm-heading-row flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
        <span className="qm-step" aria-hidden="true">02</span>
        <span className="qm-panel-title">Materials List</span>
        <span className="qm-heading-meta">
        <NewFeatureBadge testId="badge-materials-list-new" />
        <span className="qm-private-tag">Builder only</span>
        <span className="text-xs text-muted-foreground" data-testid="text-materials-count">{distinct === 0 && data.pending.length ? "Awaiting approval" : distinct === 0 && data.missing.length ? "Material breakdown unavailable" : `${distinct} ${distinct === 1 ? "material" : "materials"}${data.missing.length || data.pending.length ? " · partial list" : ""}`}</span>
        <span className="text-sm font-semibold tabular-nums" data-testid="text-materials-subtotal">{distinct === 0 && data.missing.length ? "—" : gbp(data.subtotal)}</span>
        </span>
        <ChevronDown className="qm-heading-chevron ml-auto" size={14} aria-hidden="true" />
      </div>
    </summary>
    <p className="qm-scope-note">Procurement costs only. Order quantities use whole purchase units where sizes are known; costs remain measured estimates. Confirm whole-pack charges before ordering. Selling prices stay with the work items; labour and plant are in each line’s breakdown.</p>
    <fieldset disabled={saving} className="qm-panel-content space-y-4 min-w-0">
      {data.pending.length > 0 && <p className="qm-pending-note" role="status">
        {data.pending.length} {data.pending.length === 1 ? "work item is" : "work items are"} awaiting materials approval above.
      </p>}
      {data.included.length > 0
        ? <Rows rows={data.included} label="Included" subtotal={data.subtotal} roundingExtra={data.roundingExtra} orderAdjustment={data.orderAdjustment} testid="included" props={handlers} />
        : <p className="text-sm text-muted-foreground rounded-md border border-dashed p-3">{data.pending.length
          ? "Approve materials in Materials & Pricing Review to add them here." : !data.missing.length && !data.optional.length
          ? "No materials to purchase for these work items." : "No itemised included materials yet. Purchase materials will appear here as they are identified."}</p>}
      {data.optional.length > 0 && <Rows rows={data.optional} label="Optional items" subtotal={data.optionalSubtotal} roundingExtra={data.optionalRoundingExtra} orderAdjustment={data.optionalOrderAdjustment} testid="optional" props={handlers} />}
      {data.missing.length > 0 && <div className="qm-missing" data-testid="group-not-broken-down">
        <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2" role="note">
          <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800 dark:text-amber-300"><span className="font-semibold">Not broken down.</span>{" "}
            Partial materials list — these work items are not included in the materials total. Values below are work item totals, not material costs.</p>
        </div>
        {data.missing.map(m => <div key={m.index} className="qm-missing-row">
          <button type="button" className="text-left break-words underline-offset-2 hover:underline" data-testid={`link-missing-${m.index}`} onClick={() => onJump(m.index)}>
            {m.name}{m.isOptional && <span className="ml-2 text-xs text-muted-foreground">optional</span>}
          </button>
          <span className="tabular-nums shrink-0">{gbp(m.subtotal)}</span>
        </div>)}
      </div>}
      <div className="border-t pt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" role="status" data-testid="status-materials-confirmation">
          {confirmed ? "Materials List confirmed. Your order list is ready below. Changes to the list require confirmation again."
            : "Check the estimated materials, then confirm to create your consolidated order list."}
        </p>
        <button type="button" data-testid="button-confirm-materials-list"
          className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"
          onClick={() => confirmed ? showOrder() : setConfirmationOpen(true)}>
          {confirmed ? "View confirmed order list" : "Confirm Materials List"}
        </button>
      </div>
    </fieldset>
  </details>
    <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="dialog-confirm-materials"
        onCloseAutoFocus={event => {
          if (revealAfterClose.current) {
            event.preventDefault();
            revealAfterClose.current = false;
            showOrder();
          }
        }}>
        <DialogHeader className="pb-1">
          <DialogTitle className="text-xl">Before You Continue</DialogTitle>
          <DialogDescription className="sr-only">Please review the Materials List carefully before proceeding.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          The Materials List is an estimate. <span className="font-semibold">It is not a final material order or a guaranteed cost for the customer.</span>
        </div>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3 items-start text-muted-foreground">
            <Ruler className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
            <span><span className="font-medium text-foreground">Check quantities and dimensions.</span>{" "}
              Some may be based on assumptions due to missing drawing information or other limitations.</span>
          </li>
          <li className="flex gap-3 items-start text-muted-foreground">
            <Eye className="h-4 w-4 shrink-0 mt-0.5 text-foreground/50" />
            <span><span className="font-medium text-foreground">Review all materials.</span>{" "}
              Materials may be missing, and specifications or quantities may need adjustment.</span>
          </li>
          {order.partial && <li className="flex gap-3 items-start text-amber-700 dark:text-amber-400" role="status">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
            This is a partial list: work awaiting materials approval or a breakdown is excluded. Materials with unknown purchase sizes remain flagged for checking.
            </span>
          </li>}
          <li className="flex gap-3 items-start rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 -mx-1">
            <Check className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <span className="text-amber-900"><span className="font-semibold">Always verify the materials, measurements and quantities</span>{" "}before placing an order.</span>
          </li>
        </ul>
        <DialogFooter className="pt-1">
          <Button type="button" variant="ghost" onClick={() => setConfirmationOpen(false)}>Cancel</Button>
          <Button type="button" disabled={saving}
            data-testid="button-create-materials-order"
            onClick={() => { confirm(); revealAfterClose.current = true; setConfirmationOpen(false); }}>
            I Understand
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {confirmed ? <div ref={orderRef} tabIndex={-1} aria-label="Confirmed Materials Order List" className="scroll-mt-24 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <MaterialsOrderList {...order} reference={reference} onJump={onJump} canDownload={!saving} />
    </div>
      : <details className="qm-panel" data-testid="materials-order-list-locked">
        <summary className="qm-panel-heading flex items-center gap-3">
          <span className="qm-step" aria-hidden="true">03</span>
          <span className="qm-panel-title">Materials Order List</span>
          <span className="qm-heading-meta text-xs text-muted-foreground">Confirm Materials List first</span>
          <ChevronDown className="qm-heading-chevron ml-auto" size={14} aria-hidden="true" />
        </summary>
        <p className="qm-scope-note">Confirm the current Materials List in Step 2 to view consolidated order quantities.</p>
      </details>}
  </>;
}
